import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LangProvider, useT } from "./lib/i18n";
import { Show } from "@clerk/react";
import { Toaster } from "./components/ui/sonner";
import ClerkAuthProvider from "./components/auth/ClerkAuthProvider";
import Layout from "./components/Layout";
import { SiteProvider, useSite } from "./lib/site";
import HomePage from "./pages/HomePage";
import Plans, { PlanDetail } from "./pages/Plans";
import ContentPage from "./pages/ContentPage";
import { Apply, SolarEnquiry, CommercialEnquiry, GrievancePage, Contact } from "./pages/FormPages";
import { Blog, BlogPost, HtmlSitemap, SignInPage, SignUpPage, PAGES } from "./pages/Misc";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import { Analytics } from "./components/Analytics";

function LocalToaster() {
  const { tl } = useT();
  return <Toaster richColors position="top-right" containerAriaLabel={tl("Notifications")} />;
}

function RouteEffects() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { redirects } = useSite();
  useEffect(() => {
    if (pathname.length > 1 && pathname.endsWith("/")) { navigate(pathname.replace(/\/+$/, ""), { replace: true }); return; }
    const r = (redirects || []).find((x) => x.from === pathname);
    if (r) { navigate(r.to, { replace: true }); return; }
    window.scrollTo(0, 0);
  }, [pathname, redirects, navigate]);
  return null;
}

const Authed = () => <ClerkAuthProvider><Outlet /></ClerkAuthProvider>;
const Protected = ({ children }) => (<><Show when="signed-in">{children}</Show><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>);

export default function App() {
  return (
    <HelmetProvider>
      <LangProvider>
      <SiteProvider>
        <BrowserRouter>
          <RouteEffects />
          <Analytics />
          <Routes>
            <Route element={<Authed />}>
              <Route path="/dashboard/*" element={<Protected><Dashboard /></Protected>} />
              <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
              <Route path="/partner/login" element={<Navigate to="/sign-in" replace />} />
              <Route element={<Layout />}>
                <Route path="/sign-in/*" element={<SignInPage />} />
                <Route path="/sign-up/*" element={<SignUpPage />} />
              </Route>
            </Route>
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/partner-plans" element={<Plans />} />
              <Route path="/partner-plans/:slug" element={<PlanDetail />} />
              {Object.keys(PAGES).map((p) => <Route key={p} path={p} element={<ContentPage />} />)}
              <Route path="/apply" element={<Apply />} />
              <Route path="/solar-enquiry" element={<SolarEnquiry />} />
              <Route path="/solar-enquiry/commercial" element={<CommercialEnquiry />} />
              <Route path="/grievance" element={<GrievancePage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/sitemap" element={<HtmlSitemap />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          <LocalToaster />
        </BrowserRouter>
      </SiteProvider>
      </LangProvider>
    </HelmetProvider>
  );
}
